/**
 * Astor Briefing DTO — borrower-scoped "Astor briefing" card on the
 * consumer dashboard.
 *
 * Renders as the warm welcome / today's-snapshot card at the top of the
 * borrower's dashboard:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Two loans need your attention today                         │
 *   │ • Sign disclosure on Case #ABC                              │
 *   │ • Upload Schedule E for refinance                           │
 *   │ Overnight: Your appraisal arrived for the refinance.        │
 *   │                                            reasoned 7s ago  │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Wave 2 — DTO contract + deterministic projection.
 * The DTO is borrower-scoped (NOT case-scoped). The agent service
 * composes it from each borrower's cases + recent activity. Future
 * waves can swap the deterministic generator for a real Astor LLM
 * dispatch without changing this contract.
 */

import { z } from 'zod';

// ============================================================================
// Tone — drives banner color + the warmth of the headline copy.
// ============================================================================

/**
 * Astor briefing tone. Maps to a banner color in the UI:
 *
 *   - `MINT`     — green/teal banner, "things are going well today"
 *   - `AMBER`    — warm amber banner, "you have action items to address"
 *   - `NEUTRAL`  — soft gray banner, "welcome back, nothing urgent"
 */
export const AstorBriefingToneSchema = z.enum(['MINT', 'AMBER', 'NEUTRAL']);
export type AstorBriefingTone = z.infer<typeof AstorBriefingToneSchema>;

// ============================================================================
// Bullets — actionable items shown under the headline.
// ============================================================================

/**
 * A single actionable bullet. The UI renders the label as the bullet
 * text and, when `href` is set, makes the bullet a deep-link the
 * borrower can click to jump straight to the action.
 */
export const AstorBriefingBulletSchema = z.object({
  id: z.string().uuid(),
  /**
   * Human-readable bullet text (e.g. "Sign disclosure on Case #ABC",
   * "Upload Schedule E for refinance"). Max 200 chars to keep the card
   * scannable.
   */
  label: z.string().min(1).max(200),
  /**
   * Optional deep-link inside the consumer portal (e.g. `/cases/abc`,
   * `/documents`). When present, the UI renders the bullet as a link.
   */
  href: z.string().optional(),
});
export type AstorBriefingBulletDto = z.infer<typeof AstorBriefingBulletSchema>;

// ============================================================================
// Root — Astor briefing envelope.
// ============================================================================

/**
 * The full briefing envelope returned by the agent service for a given
 * borrower. One briefing per borrower at a time — the briefing is
 * regenerated on each dashboard load (no caching in Wave 2).
 */
export const AstorBriefingSchema = z.object({
  tone: AstorBriefingToneSchema,
  /**
   * Single-line, warm headline. Max 200 chars to fit on the card.
   * Examples:
   *   - "Two loans need your attention today" (AMBER)
   *   - "Your refinance is on track — appraisal arrived overnight" (MINT)
   *   - "Welcome back — nothing urgent" (NEUTRAL)
   */
  headline: z.string().min(1).max(200),
  /**
   * Up to four actionable items, in priority order. Empty when there
   * are no actions for the borrower today.
   */
  bullets: z.array(AstorBriefingBulletSchema).max(4).default([]),
  /**
   * Short overnight recap (1-2 sentences). Empty string when no
   * overnight content is appropriate (e.g. recent visit, no activity).
   */
  overnight: z.string().optional().default(''),
  /**
   * ISO timestamp of when the briefing was generated. UI shows
   * "reasoned 7s ago" derived from this.
   */
  generatedAt: z.string().datetime(),
});
export type AstorBriefingDto = z.infer<typeof AstorBriefingSchema>;
